/**
 * Coin Service
 * Manages coin balance operations (award, deduct, get balance)
 */

import { supabase, supabaseRpc } from '@/lib/supabase';
import { useAnalytics } from '@/composables/useAnalytics';

const analytics = useAnalytics();

export const coinService = {
  /**
   * Award 1 coin to a child (typically called after task completion)
   * @param childId UUID of the child receiving the coin
   * @param source Source of coin ('task_completion', 'level_chest', etc.)
   * @param referenceId Optional ID of related entity (task_id, level_chest_id, etc.)
   * @returns New coin balance after award
   */
  async awardCoin(
    childId: string,
    source: string = 'task_completion',
    referenceId?: string,
    retries: number = 3
  ): Promise<number> {
    console.log('[coinService] Awarding coin to child:', childId, 'source:', source, 'referenceId:', referenceId);
    
    let lastError: Error | null = null;
    
    // Retry logic for network failures or transient errors
    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        // Use RPC function for atomic operation (prevents race conditions from concurrent updates)
        // The database function handles the update atomically, preventing conflicts
        const { data: newBalance, error: rpcError } = await supabaseRpc.rpc('award_coin_to_child', {
          p_child_id: childId,
          p_source: source,
          p_reference_id: referenceId || null
        });

        if (rpcError) {
          // Check if it's a transient error (network, timeout) that we should retry
          const isTransientError = rpcError.code === 'PGRST301' || // Network error
                                   rpcError.message?.includes('timeout') ||
                                   rpcError.message?.includes('network') ||
                                   rpcError.code === '08006'; // Connection failure
          
          if (isTransientError && attempt < retries - 1) {
            console.warn(`[coinService] Transient error on attempt ${attempt + 1}, retrying...`, rpcError);
            lastError = new Error(`Failed to award coin: ${rpcError.message}`);
            // Exponential backoff: wait 100ms, 200ms, 400ms
            await new Promise(resolve => setTimeout(resolve, 100 * Math.pow(2, attempt)));
            continue;
          }
          
          console.error('[coinService] Error awarding coin via RPC:', rpcError);
          throw new Error(`Failed to award coin: ${rpcError.message}`);
        }

        if (newBalance === null || newBalance === undefined) {
          throw new Error('Failed to award coin: No balance returned');
        }

        console.log('[coinService] Coin awarded successfully, new balance:', newBalance);
        
        // Track coin earned in analytics
        try {
          await analytics.logCoinEarned(childId, 1, source, referenceId);
        } catch (analyticsError) {
          // Don't fail coin award if analytics fails
          console.warn('[coinService] Analytics tracking failed (non-critical):', analyticsError);
        }
        
        return Number(newBalance);
      } catch (error: any) {
        lastError = error;
        // If it's not a transient error or we've exhausted retries, throw
        if (attempt === retries - 1 || !error.message?.includes('timeout') && !error.message?.includes('network')) {
          console.error('[coinService] Error in awardCoin after retries:', error);
          throw error;
        }
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, 100 * Math.pow(2, attempt)));
      }
    }
    
    // If we get here, all retries failed
    throw lastError || new Error('Failed to award coin after retries');
  },

  /**
   * Deduct coins from a child (typically for shop purchases or task uncompletion)
   * @param childId UUID of the child
   * @param amount Amount to deduct (must be positive)
   * @param source Source of deduction ('shop_purchase', 'task_uncomplete', etc.)
   * @param referenceId Optional ID of related entity
   * @returns New coin balance after deduction
   */
  async deductCoin(
    childId: string,
    amount: number,
    source: string = 'shop_purchase',
    referenceId?: string,
    retries: number = 3
  ): Promise<number> {
    if (amount <= 0) {
      throw new Error('Invalid amount: amount must be positive');
    }

    let lastError: Error | null = null;
    
    // Retry logic for network failures or transient errors
    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        // Use RPC function for atomic operation (prevents race conditions from concurrent updates)
        // The database function handles balance check and deduction atomically
        const { data: newBalance, error: rpcError } = await supabaseRpc.rpc('deduct_coins_from_child', {
          p_child_id: childId,
          p_amount: amount,
          p_source: source,
          p_reference_id: referenceId || null
        });

        if (rpcError) {
          // Check if it's an insufficient coins error (don't retry)
          if (rpcError.message?.includes('Insufficient coins')) {
            throw new Error('Insufficient coins');
          }
          
          // Check if it's a transient error (network, timeout) that we should retry
          const isTransientError = rpcError.code === 'PGRST301' || // Network error
                                   rpcError.message?.includes('timeout') ||
                                   rpcError.message?.includes('network') ||
                                   rpcError.code === '08006'; // Connection failure
          
          if (isTransientError && attempt < retries - 1) {
            console.warn(`[coinService] Transient error on attempt ${attempt + 1}, retrying...`, rpcError);
            lastError = new Error(`Failed to deduct coins: ${rpcError.message}`);
            // Exponential backoff: wait 100ms, 200ms, 400ms
            await new Promise(resolve => setTimeout(resolve, 100 * Math.pow(2, attempt)));
            continue;
          }
          
          console.error('[coinService] Error deducting coins via RPC:', rpcError);
          throw new Error(`Failed to deduct coins: ${rpcError.message}`);
        }

        if (newBalance === null || newBalance === undefined) {
          throw new Error('Failed to deduct coins: No balance returned');
        }

        console.log('[coinService] Coins deducted successfully, new balance:', newBalance);
        
        // Track coin spent in analytics
        try {
          await analytics.logCoinSpent(childId, amount, source, referenceId);
        } catch (analyticsError) {
          // Don't fail coin deduction if analytics fails
          console.warn('[coinService] Analytics tracking failed (non-critical):', analyticsError);
        }
        
        return Number(newBalance);
      } catch (error: any) {
        lastError = error;
        // Check if it's an insufficient coins error (don't retry)
        if (error.message?.includes('Insufficient coins')) {
          throw new Error('Insufficient coins');
        }
        // If it's not a transient error or we've exhausted retries, throw
        if (attempt === retries - 1 || (!error.message?.includes('timeout') && !error.message?.includes('network'))) {
          console.error('[coinService] Error in deductCoin after retries:', error);
          throw error;
        }
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, 100 * Math.pow(2, attempt)));
      }
    }
    
    // If we get here, all retries failed
    throw lastError || new Error('Failed to deduct coins after retries');
  },

  /**
   * Get current coin balance for a child
   * @param childId UUID of the child
   * @returns Current coin balance
   */
  async getCoinBalance(childId: string): Promise<number> {
    const { data, error } = await (supabase as any)
      .from('children')
      .select('coins, points')
      .eq('id', childId)
      .single();

    if (error) {
      console.error('Error getting coin balance:', error);
      throw new Error(`Failed to get coin balance: ${error.message}`);
    }

    if (!data) {
      throw new Error('Child not found');
    }

    return Number((data as any).coins ?? (data as any).points ?? 0);
  },

  /**
   * Get coin balances for multiple children in one batch query
   * @param childIds Array of child IDs to fetch balances for
   * @returns Map of childId -> coin balance (with fallback to points, else 0)
   */
  async getCoinBalancesForChildren(childIds: string[]): Promise<Map<string, number>> {
    const out = new Map<string, number>();
    if (!childIds.length) return out;

    const { data, error } = await (supabase as any)
      .from('children')
      .select('id, coins, points')
      .in('id', childIds);

    if (error) {
      console.error('[coinService] Error getting coin balances:', error);
      throw new Error(`Failed to get coin balances: ${error.message}`);
    }

    for (const row of (data ?? []) as any[]) {
      out.set(row.id, Number(row.coins ?? row.points ?? 0));
    }

    return out;
  }
};
